import { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { getChantiersForUser } from '../lib/chantierService';
import ChantierCard from './ChantierCard';
import NewChantierModal from './NewChantierModal';
import '../App.css';
import './ChantiersList.css';

// Nombre de chantiers chargés au départ, puis à chaque « Afficher plus ».
const PAGE_SIZE = 9;

// Message affiché quand la liste ne se charge pas : jamais le détail technique (gardé dans la console).
function loadErrorMessage(err) {
  const offline = typeof navigator !== 'undefined' && navigator.onLine === false;
  const network = /failed to fetch|networkerror|load failed|network request failed/i.test(err?.message || '');
  if (offline || network) {
    return 'Vos projets n’ont pas pu être chargés : la connexion internet semble interrompue. Vérifiez-la puis réessayez.';
  }
  return 'Vos projets n’ont pas pu être chargés suite à un problème technique de notre côté. Réessayez dans quelques instants ; si le problème persiste, contactez le support MyTravo.';
}

function ChantiersList() {
  const { session } = useAuth();
  const userId = session?.user?.id;
  const [chantiers, setChantiers] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [loadMoreError, setLoadMoreError] = useState(null);
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  // Nombre de chantiers affichés : un rafraîchissement (création, mise à jour) recharge autant de
  // chantiers qu'avant, pour ne pas replier la liste.
  const visibleCountRef = useRef(PAGE_SIZE);

  const loadChantiers = useCallback(() => {
    if (!userId) return;

    let cancelled = false;
    setLoading(true);
    setError(null);
    setLoadMoreError(null);

    getChantiersForUser(userId, { offset: 0, limit: Math.max(PAGE_SIZE, visibleCountRef.current) })
      .then((page) => {
        if (cancelled) return;
        setChantiers(page.chantiers);
        setTotal(page.total);
        visibleCountRef.current = Math.max(PAGE_SIZE, page.chantiers.length);
      })
      .catch((err) => {
        console.error('Chargement des chantiers impossible', err);
        if (!cancelled) setError(loadErrorMessage(err));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [userId]);

  useEffect(() => {
    const cancel = loadChantiers();
    return cancel;
  }, [loadChantiers]);

  const loadMore = async () => {
    setLoadingMore(true);
    setLoadMoreError(null);
    try {
      const page = await getChantiersForUser(userId, { offset: chantiers.length, limit: PAGE_SIZE });
      // un chantier créé entre-temps décale les pages : on ignore ceux déjà affichés
      const shown = new Set(chantiers.map((c) => c.id));
      const next = [...chantiers, ...page.chantiers.filter((c) => !shown.has(c.id))];
      setChantiers(next);
      setTotal(page.total);
      visibleCountRef.current = next.length;
    } catch (err) {
      console.error('Chargement des chantiers suivants impossible', err);
      setLoadMoreError(loadErrorMessage(err));
    } finally {
      setLoadingMore(false);
    }
  };

  const handleChantierCreated = () => {
    setIsCreateOpen(false);
    loadChantiers();
  };

  return (
    <div className="App">
      <div className="App-card ChantiersList-card">
        <div className="ChantiersList-head">
          <div>
            <h1 className="ChantiersList-title">Mes projets</h1>
            <p className="App-subtitle">Portail web de l'application mobile</p>
          </div>
          <button className="ChantiersList-createBtn" onClick={() => setIsCreateOpen(true)}>
            + Créer un projet
          </button>
        </div>

        {loading && chantiers.length === 0 && (
          <div className="ChantiersList-loading">
            <span className="Spinner" />
            <p>Chargement des chantiers…</p>
          </div>
        )}

        {error && !loading && (
          <div className="ChantiersList-error" role="alert">
            <p>{error}</p>
            <button className="ChantiersList-retryBtn" onClick={loadChantiers}>
              Réessayer
            </button>
          </div>
        )}

        {!loading && !error && chantiers.length === 0 && (
          <p className="ChantiersList-empty">Aucun chantier pour le moment.</p>
        )}

        {!error && chantiers.length > 0 && (
          <>
            <div className="ChantiersList-items">
              {chantiers.map((chantier) => (
                <ChantierCard key={chantier.id} chantier={chantier} userId={userId} onUpdated={loadChantiers} />
              ))}
            </div>

            {(chantiers.length < total || loadMoreError) && (
              <div className="ChantiersList-more">
                {loadMoreError && (
                  <p className="ChantiersList-moreError" role="alert">
                    {loadMoreError}
                  </p>
                )}
                <button className="ChantiersList-moreBtn" onClick={loadMore} disabled={loadingMore}>
                  {loadingMore ? 'Chargement…' : 'Afficher plus'}
                </button>
                <span className="ChantiersList-count">
                  {chantiers.length} sur {total}
                </span>
              </div>
            )}
          </>
        )}
      </div>

      {isCreateOpen && (
        <NewChantierModal
          onClose={() => setIsCreateOpen(false)}
          onCreated={handleChantierCreated}
        />
      )}
    </div>
  );
}

export default ChantiersList;
